import type { Root as MdRoot, RootContent } from "mdast";
import type { Root as HtmlRoot, Element } from "hast";
import type { VFile } from "vfile";
import type { Processor } from "unified";
import type { Code, State, Tokenizer } from "micromark-util-types";
import type {} from "micromark-extension-math";
import type {} from "remark-parse";
import remarkMath from "remark-math";

const whitespace = (code: Code) => code !== null && (code < 0 || /\s/u.test(String.fromCharCode(code)));

/** Single-dollar math keeps the platform's guards against prices and partial formulas. */
const tokenizeInlineMath: Tokenizer = function(effects, ok, nok) {
	const thisPrevious = this.previous;
	let previous: Code = null;
	let escaped = false;
	return start;

	function start(code: Code): State | undefined {
		if (thisPrevious === 36) return nok(code);
		effects.enter("mathText");
		effects.enter("mathTextSequence");
		effects.consume(code);
		effects.exit("mathTextSequence");
		return first;
	}

	function first(code: Code): State | undefined {
		if (code === null || code === 36 || whitespace(code)) return nok(code);
		effects.enter("mathTextData");
		return inside(code);
	}

	function inside(code: Code): State | undefined {
		if (code === null || code === -5 || code === -4 || code === -3) return nok(code);
		if (code === 36 && !escaped) {
			if (whitespace(previous)) return nok(code);
			effects.exit("mathTextData");
			effects.enter("mathTextSequence");
			effects.consume(code);
			effects.exit("mathTextSequence");
			effects.exit("mathText");
			return after;
		}
		effects.consume(code);
		escaped = !escaped && code === 92;
		previous = code;
		return inside;
	}

	function after(code: Code): State | undefined {
		return code !== null && code >= 48 && code <= 57 ? nok(code) : ok(code);
	}

};

export function remarkSafeMath(this: Processor) {
	remarkMath.call(this, { singleDollarTextMath: false });
	const data = this.data();
	(data.micromarkExtensions ??= []).push({ text: { 36: { tokenize: tokenizeInlineMath } } });
	return remarkMathHosts();
}

/** Route formulas to the existing safe, lazy React typesetter. */
function remarkMathHosts() {
	return (tree: MdRoot, file: VFile) => {
		function walk(node: MdRoot | RootContent) {
			if (node.type === "math" || node.type === "inlineMath") {
				const start = node.position?.start.offset ?? 0;
				const display = node.type === "math" || String(file.value).slice(start, start + 2) === "$$";
				const tex = node.type === "math" ? [node.meta, node.value].filter(Boolean).join("\n").trim() : node.value;
				node.data = { hName: "span", hProperties: { dataTex: tex, dataDisplay: display }, hChildren: [] };
			} else if ("children" in node) {
				for (const child of node.children) walk(child);
			}
		}
		walk(tree);
	};
}

/** Expose prose runs to platform controls without entering code, math or anchors. */
export function rehypeProse() {
	return (tree: HtmlRoot) => {
		function walk(node: HtmlRoot | Element) {
			if (node.type === "element" && (["a", "code", "pre"].includes(node.tagName) || typeof node.properties.dataTex === "string")) return;
			for (let i = 0; i < node.children.length; i++) {
				const child = node.children[i]!;
				if (child.type === "text" && /\S/.test(child.value)) node.children[i] = { type: "element", tagName: "span", properties: { dataProse: true }, children: [child] };
				if (child.type === "element") walk(child);
			}
		}
		walk(tree);
	};
}
